// Step 5 — Preview + confirm (Phase 2 single-UGC + Phase 7 batch).
//
// Single-UGC: summary of the selections, seed-cascade preview, Generate.
// Batch: per-UGC breakdown, total-dispatches count, mandatory checkbox
// acknowledgement (Phase 7 W2 — batch generation is real money and the
// operator must tick before Generate becomes clickable).
//
// The shell owns the actual generate dispatch (one /api/ads/generate
// call per UGC × campaign combo — W1); this component only surfaces
// the state and the acknowledgement gate.

import { HStack, VStack, Box, Text, Badge, Divider, Checkbox, Image, Alert, AlertIcon } from '@chakra-ui/react';
import type { MediaAssignments, WizardSelections } from './types';

type Props = {
  value:              WizardSelections;
  onChange:           (patch: Partial<WizardSelections>) => void;
  assignments:        MediaAssignments | null;
  assignmentsByMedia: Map<string, MediaAssignments>;
};

// Backend's meta_all preset fans each concept out to Meta static +
// video surfaces. Concept count comes from the Director round (currently
// 3 concepts × 5 templates, but Director chooses the actual template
// per concept). Rough per-UGC generation count assumes 3 concepts × ~4
// aspects, which matches what production runs deliver at meta_all. Not
// a hard estimate — the Confirm page's job is to make batch size real,
// not to be a billing quote.
const APPROX_ADS_PER_UGC = 12;

export function Step5Confirm({ value, onChange, assignments, assignmentsByMedia }: Props) {
  const isBatch = value.mediaIds.length > 1;

  const targetSummary = (() => {
    if (value.path === 'product')     return `${value.productIds.length} product${value.productIds.length === 1 ? '' : 's'}`;
    if (value.path === 'category')    return `${value.categoryIds.length} categor${value.categoryIds.length === 1 ? 'y' : 'ies'}`;
    if (value.path === 'brand')       return 'Brand identity signal';
    if (value.path === 'promotional') return `Promotional${value.calloutProductIds.length ? ` (+${value.calloutProductIds.length} callouts)` : ''}`;
    return '—';
  })();

  const campaignSummary = value.campaignId
    ? 'Existing campaign'
    : value.newCampaign.name
      ? `New: ${value.newCampaign.name}`
      : '(none)';

  const approxTotalAds = APPROX_ADS_PER_UGC * value.mediaIds.length;

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
          Review
        </Text>
        <Text fontSize="xs" color="brand.muted">
          Confirm the setup and hit Generate — the backend expands the ad cartesian on submit.
          Exact ad count depends on template + platform-format fanout resolved server-side.
        </Text>
      </Box>

      <VStack align="stretch" spacing={2} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface" p={4}>
        <Row label="UGC" value={
          isBatch
            ? `${value.mediaIds.length} UGCs (batch)`
            : (assignments?.media.externalId || value.mediaIds[0] || '(none)')
        } />
        <Row label="Path"     value={value.path} badge />
        <Row label="Target"   value={targetSummary} />
        <Row label="Campaign" value={campaignSummary} />
        {value.path === 'promotional' && value.newCampaign.offer && (
          <Row label="Offer"  value={value.newCampaign.offer} />
        )}
        {value.path === 'promotional' && value.newCampaign.endDate && (
          <Row label="Ends"   value={value.newCampaign.endDate} />
        )}
        {isBatch && (
          <Row label="Dispatches" value={`${value.mediaIds.length} generate calls (one per UGC)`} />
        )}
      </VStack>

      {isBatch && (
        <BatchBreakdown mediaIds={value.mediaIds} assignmentsByMedia={assignmentsByMedia} approxAdsPerUgc={APPROX_ADS_PER_UGC} />
      )}

      <Divider />

      <Box>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
          Seed cascade
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Badge fontSize="10px" colorScheme="purple" variant="solid">
            {isBatch ? `Each UGC → its own index 0` : 'UGC (index 0)'}
          </Badge>
          {value.path === 'product'     && <Badge fontSize="10px" colorScheme="gray" variant="subtle">→ product hero</Badge>}
          {value.path === 'product'     && <Badge fontSize="10px" colorScheme="gray" variant="subtle">→ alts</Badge>}
          {value.path === 'category'    && <Badge fontSize="10px" colorScheme="gray" variant="subtle">→ category products</Badge>}
          {value.path === 'brand'       && <Badge fontSize="10px" colorScheme="gray" variant="subtle">→ brand identity signals</Badge>}
          {value.path === 'promotional' && <Badge fontSize="10px" colorScheme="gray" variant="subtle">→ callouts + offer</Badge>}
        </HStack>
        <Text fontSize="10px" color="brand.muted" mt={2}>
          UGC-first seeding is applied server-side (feature-flagged via UGC_FIRST_SEEDING). Each UGC lands at seed index 0 of its own generation.
          {isBatch && ' In batch mode, N /api/ads/generate calls fire sequentially (one per UGC), each carrying its own preferUgcMediaId.'}
        </Text>
      </Box>

      {isBatch && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="700" mb={1}>
              Batch generation — ~{approxTotalAds} ads across {value.mediaIds.length} UGCs
            </Text>
            <Text fontSize="xs" mb={2}>
              This is real money — each generation calls the image + video pipelines. Rough per-UGC estimate is {APPROX_ADS_PER_UGC} ads (3 concepts × ~4 aspects on meta_all); the exact count depends on Director output and template fan-out.
            </Text>
            <Checkbox
              isChecked={value.batchAcknowledged}
              onChange={(e) => onChange({ batchAcknowledged: e.target.checked })}
              colorScheme="orange"
            >
              <Text fontSize="sm" fontWeight="700">
                I understand and want to generate ~{approxTotalAds} ads across {value.mediaIds.length} UGCs.
              </Text>
            </Checkbox>
          </Box>
        </Alert>
      )}
    </VStack>
  );
}

// Per-UGC × per-target breakdown table. Currently a single-campaign
// wizard (Phase 7 doesn't add multi-campaign selection), so the columns
// are UGC × (approxAds). Multi-campaign would extend the columns.
function BatchBreakdown({
  mediaIds, assignmentsByMedia, approxAdsPerUgc
}: {
  mediaIds:           string[];
  assignmentsByMedia: Map<string, MediaAssignments>;
  approxAdsPerUgc:    number;
}) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface" p={3}>
      <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        Batch breakdown
      </Text>
      <VStack align="stretch" spacing={1} maxH="260px" overflowY="auto">
        {mediaIds.map((id, i) => {
          const a = assignmentsByMedia.get(id);
          return (
            <HStack key={id} spacing={2} p={1.5} borderWidth="1px" borderColor="brand.border" borderRadius="sm" bg="white">
              <Text fontSize="10px" color="brand.muted" w="20px" textAlign="right">{i + 1}</Text>
              <Box boxSize="32px" bg="brand.canvas" borderRadius="sm" overflow="hidden" flexShrink={0}>
                {a?.media.fileUrl ? (
                  a.media.fileType === 'video' ? (
                    <video src={a.media.fileUrl} muted loop playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <Image src={a.media.fileUrl} alt="" w="100%" h="100%" objectFit="cover" />
                  )
                ) : null}
              </Box>
              <Text fontSize="xs" fontWeight="700" color="brand.ink" flex={1} noOfLines={1}>
                {a?.media.externalId || id.slice(-8)}
              </Text>
              <Text fontSize="10px" color="brand.muted">~{approxAdsPerUgc} ads</Text>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}

function Row({ label, value, badge }: { label: string; value: string; badge?: boolean }) {
  return (
    <HStack spacing={3}>
      <Text fontSize="11px" fontWeight="700" color="brand.muted" minW="80px">{label}</Text>
      {badge ? (
        <Badge fontSize="10px" colorScheme="purple" variant="subtle">{value}</Badge>
      ) : (
        <Text fontSize="sm" color="brand.ink" fontWeight="700">{value}</Text>
      )}
    </HStack>
  );
}
